import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { format, parseISO, addDays } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface ForecastDataPoint {
    date: string;
    actual?: number;
    forecast?: number;
    upper?: number;
    lower?: number;
    confidenceBand?: [number, number];
}

export const RevenueForecastChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): ForecastDataPoint[] => {
        if (!finance?.dailyBreakdown) return [];

        const sorted = Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return { date, value: metrics?.revenue ?? 0 };
            });

        if (sorted.length < 2) return [];

        // Double Exponential Smoothing (Holt's method)
        const alpha = 0.3;
        const beta = 0.1;
        const values = sorted.map(d => d.value);

        // Initialize
        const level: number[] = [values[0]];
        const trend: number[] = [0];

        // Compute initial trend from first few differences
        const initCount = Math.min(values.length - 1, 5);
        let trendSum = 0;
        for (let i = 0; i < initCount; i++) {
            trendSum += values[i + 1] - values[i];
        }
        trend[0] = trendSum / initCount;

        // Apply smoothing
        for (let i = 1; i < values.length; i++) {
            level[i] = alpha * values[i] + (1 - alpha) * (level[i - 1] + trend[i - 1]);
            trend[i] = beta * (level[i] - level[i - 1]) + (1 - beta) * trend[i - 1];
        }

        // Build actual data points
        const result: ForecastDataPoint[] = sorted.map(d => ({
            date: d.date,
            actual: d.value,
        }));

        // Generate 14-day forecast
        const lastDate = parseISO(sorted[sorted.length - 1].date);
        const lastLevel = level[level.length - 1];
        const lastTrend = trend[trend.length - 1];

        for (let k = 1; k <= 14; k++) {
            const forecastValue = Math.max(0, lastLevel + k * lastTrend);
            const margin = forecastValue * 0.15;
            const forecastDate = format(addDays(lastDate, k), 'yyyy-MM-dd');
            result.push({
                date: forecastDate,
                forecast: forecastValue,
                upper: forecastValue + margin,
                lower: Math.max(0, forecastValue - margin),
                confidenceBand: [Math.max(0, forecastValue - margin), forecastValue + margin],
            });
        }

        // Connect actual to forecast: add forecast value to the last actual point
        const lastActualIdx = sorted.length - 1;
        result[lastActualIdx].forecast = result[lastActualIdx].actual;

        return result;
    }, [finance?.dailyBreakdown]);

    const forecastEnd = useMemo(() => {
        const forecasted = chartData.filter(d => d.forecast !== undefined && d.actual === undefined);
        if (forecasted.length === 0) return null;
        return forecasted[forecasted.length - 1].forecast;
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty && forecastEnd != null ? (
            <span style={{ color: CHART_COLORS.revenue }}>
                {t('charts.forecastLabel', 'Forecast')}: {formatCompactNumber(forecastEnd, currencySymbol)}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <defs>
                            <linearGradient id="revForecastBand" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0.03} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => {
                                const label = name === 'actual'
                                    ? t('charts.actualRevenue', 'Actual Revenue')
                                    : name === 'forecast'
                                        ? t('charts.forecastRevenue', 'Forecast Revenue')
                                        : name;
                                return [formatCurrency(value, currencySymbol), label];
                            }}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Area
                            type="monotone"
                            dataKey="confidenceBand"
                            fill="url(#revForecastBand)"
                            stroke="none"
                            name={t('charts.confidenceBand', 'Confidence Band')}
                            tooltipType="none"
                            legendType="none"
                        />
                        <Line
                            type="monotone"
                            dataKey="actual"
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={false}
                            activeDot={CHART_STYLES.line.activeDot}
                            name="actual"
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="forecast"
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            strokeDasharray="6 4"
                            strokeOpacity={0.6}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.revenue }}
                            name="forecast"
                            connectNulls={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
